import React, { useState, useEffect } from "react";
import { Field, reduxForm } from "redux-form";
import { fieldLength4, number, required } from "../../../utils/formValidators";
import { renderField } from "../../../utils/renderField";
import { AuthPaymentContainer } from "../../authenticated-payments/misc/auth-payment-container.component";
import { CardControlsRestartButton } from "../misc/card-controls-restart-button.component";
import Show from "../../../utils/show.component";

const CardResetPinFormComponent = (props) => {
  const thisForm = props.thisForm;
  const values = thisForm && thisForm.values ? thisForm.values : {};

  // show the pins initially so that the form is rendered with the input types set to text.
  // the main reason for this is to avoid having password managers try to create passwords for the pin
  // fields but still get the text masking that the password type provides.
  // once the user types a character, change the input to password so the fields are masked by default.
  const [showPIN, setShowPIN] = useState(true);
  const [showResetPIN, setShowResetPIN] = useState(true);

  const [hidePinInitialized, setHidePinInitialized] = useState(false);
  const [hidePinResetInitialized, setHidePinResetInitialized] = useState(false);

  const [isPINValid, setIsPINValid] = useState(true);
  const [isReEnterPINValid, setIsReEnterPINValid] = useState(true);
  const [notLast4SSN, setNotLast4SSN] = useState(false);

  const [validationMessages, setValidationMessages] = useState({
    length: false,
    notSequential: false,
    notSameDigits: false
  });

  const togglePINVisibility = () => {
    setShowPIN(!showPIN);
  };

  const toggleResetPINVisibility = () => {
    setShowResetPIN(!showResetPIN);
  };

  const showPINMatchError = () => {
    return !isReEnterPINValid && values.reEnterCardPin.length > 0;
  };

  const handlePinChange = (e) => {
    if (!hidePinInitialized) {
      setShowPIN(false);
      setHidePinInitialized(true);
    }
    const newPin = e.target.value;
    setIsReEnterPINValid(values.reEnterCardPin === e.target.value)
    validatePin(newPin);
  };

  const validatePin = (pin) => {
    if (pin === "" || pin === null) {
      setValidationMessages({
        length: false,
        notSequential: false,
        notSameDigits: false,
      });
      setNotLast4SSN(false);
    } else {
      const lengthValid = pin.length === 4;
      const notSequential = (/^(?!1234|2345|3456|4567|5678|6789|7890|0123|4321|5432|6543|7654|8765|9876|0987)\d{4}$/.test(pin)) || 
      // We don't want to show the error message if the PIN isn't 4 digits, regardless of sequentiality
      pin.length !== 4;
      const notSameDigits = !/(\d)\1{3}/.test(pin);
      if (lengthValid) {
        props.callPinValidation(props.cardInfo.agreeNbr, pin);
      }

      const allValid = lengthValid && notSequential && notSameDigits;
      setValidationMessages({
        length: lengthValid,
        notSequential: notSequential,
        notSameDigits: notSameDigits,
      });

      setIsPINValid(allValid);
    }
  };

  useEffect(() => {
    if (props.cardControlsInfo.validatePin !== undefined) {
      setNotLast4SSN(!props.cardControlsInfo.validatePin.MatchSSNLastFour);
    }
  }, [props.cardControlsInfo]);

  const submitForm = () => {
    window.scrollTo(0, 0);
    if(thisForm.values.cardPin === thisForm.values.reEnterCardPin && isPINValid && notLast4SSN){
      props.callPinUpdate(
        props.cardInfo.agreeNbr,
        props.thisForm.values.cardPin,
        confirmCodeCallback
      );
    }
  };

  const confirmCodeCallback = (response) => {
    if (response.Success) {
      // Advance the step of the flow parent to the next step
      props.setProcessStep(3);
    }
  };

  const handleReEnterPinChange = (e) => {
    if (!hidePinResetInitialized) {
      setShowResetPIN(false);
      setHidePinResetInitialized(true);
    }
    setIsReEnterPINValid(values.cardPin === e.target.value)
  };

  return (
    <AuthPaymentContainer title={"Reset PIN"}>
      <form onSubmit={props.handleSubmit(submitForm)}>
        <div className="row bold px-3 py-3">
          <h3 className="bold">Please enter your new PIN below</h3>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <Field
                id="pin"
                component={renderField}
                label="New PIN"
                name="cardPin"
                validate={[number, required]}
                type={showPIN ? "text" : "password"}
                hasOtherIcon={true}
                inputMode="decimal"
                showHideOnClick={togglePINVisibility}
                showHideIcon={!showPIN}
                onChange={handlePinChange}
              />
              <Show condition={!isPINValid && notLast4SSN}>
                <div className="mt-2 error-message">
                  Requested PIN does not meet all requirements. Please enter a new PIN.
                </div>
              </Show>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <Field
                id="reenterPin"
                component={renderField}
                label="Re-Enter New PIN"
                name="reEnterCardPin"
                validate={[number, required]}
                type={showResetPIN ? "text" : "password"}
                hasOtherIcon={true}
                inputMode="decimal"
                showHideOnClick={toggleResetPINVisibility}
                showHideIcon={!showResetPIN}
                onChange={handleReEnterPinChange}
              />
            </div>
            <Show condition={showPINMatchError()}>
              <div className="mt-2 error-message">
                PIN does not match. Please re-enter PIN.
              </div>
            </Show>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <span style={{ color: validationMessages.length ? "green" : "red" }}>
              {validationMessages.length ? "✔" : "✘"}{" "}
            </span>
            <span>Must be 4 digits</span>
            <br />
            <span style={{ color: validationMessages.notSameDigits ? "green" : "red" }}>
              {validationMessages.notSameDigits ? "✔" : "✘"}{" "}
            </span>
            <span> Cannot be the same 4 numbers (ex: 1111)</span>
            <br />
            <span style={{ color: validationMessages.notSequential ? "green" : "red" }}>
              {validationMessages.notSequential ? "✔" : "✘"}{" "}
            </span>
            <span>Cannot be 4 sequential numbers (ex: 1234, 6789)</span>
            <br />
            <span style={{ color: notLast4SSN ? "green" : "red" }}>
              {notLast4SSN ? "✔" : "✘"}{" "}
            </span>
            <span> Cannot be the last 4 digits of your SSN</span>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <button disabled={ !isReEnterPINValid } className="button primary full" type="submit">
              Submit
            </button>
          </div>
        </div>
        <CardControlsRestartButton backToCards={props.backToCards} buttonText="Cancel" />
      </form>
    </AuthPaymentContainer>
  );
};

// Connect to Redux store
export const CardResetPinForm = reduxForm({
  form: "ResetPin",
  destroyOnUnmount: false,
  initialValues: {
    cardPin: "",
    reEnterCardPin: "",
  },
})(CardResetPinFormComponent);



// WEBPACK FOOTER //
// ./src/components/card-controls/forms/card-reset-pin-form.component.js